import { useEffect, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useDispatch } from 'react-redux';
import isEqual from 'lodash/isEqual';
import produce from 'immer';
import { useTheme } from '@mui/material/styles';

import { reorderColumn } from 'shared/functions';

export const useTable = ({
  paginationLimit,
  entityId,
  initialColumns,
  columnsFilterKeysMapping,
  getTableDataAction,
  getTableDataActionParams,
  constantFilter = {},
  initialFilter = {},
  initialSort,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [pagination, setPagination] = useState({
    page: 0,
    limit: paginationLimit || 200,
    sort: initialSort,
  });

  const [columns, setColumns] = useState(initialColumns);

  const [refreshTable, setRefreshTable] = useState(undefined);

  const setDirection = direction => {
    switch (direction) {
      case 'asc':
        return 'DESC';
      case 'desc':
        return 'ASC';
      default:
        return 'none';
    }
  };

  const onTableChange = tableState => {
    setPagination(() => ({
      ...pagination,
      page: tableState.page,
      limit: tableState.rowsPerPage,
    }));
  };

  const onSortChange = (column, direction) => {
    setPagination(() => ({
      ...pagination,
      sort: {
        sortColumn: column,
        direction: setDirection(direction),
      },
    }));
    setColumns(reorderColumn(column, columns));
  };

  const [filter, setFilter] = useState({ ...constantFilter, ...initialFilter });
  const isFiltered = !isEqual(constantFilter, filter) || !isEqual(initialFilter, filter);

  useDeepCompareEffect(() => {
    setFilter({ ...constantFilter, ...initialFilter });
  }, [constantFilter, initialFilter]);

  useDeepCompareEffect(() => {
    if (!getTableDataAction) {
      return;
    }

    const { page, limit, sort } = pagination;

    const refreshTableFn = getTableDataAction({
      ...(entityId && { entityId }),
      page: page + 1,
      limit,
      sort,
      filter,
      ...getTableDataActionParams,
    });

    setRefreshTable(refreshTableFn);

    dispatch(refreshTableFn);
  }, [pagination, filter, getTableDataActionParams]);

  const filteredCellHeaderPropsFn = () => ({
    style: {
      color: theme.palette.primary.main,
    },
  });

  const highlightFilteredColumns = () => {
    setColumns(
      columns.map((column, index) => {
        const filterKey = columnsFilterKeysMapping[index];

        if (filterKey === null) return column;

        const filterKeys = Array.isArray(filterKey) ? filterKey : [filterKey];

        const shouldHighlightColumn = !!filterKeys.find(key => key in filter);

        return produce(column, draftColumn => {
          /* eslint-disable-next-line no-param-reassign */
          draftColumn.options.setCellHeaderProps = shouldHighlightColumn
            ? filteredCellHeaderPropsFn
            : undefined;

          return draftColumn;
        });
      }),
    );
  };

  useEffect(() => {
    setColumns(initialColumns);
    if (columnsFilterKeysMapping) {
      highlightFilteredColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColumns]);

  useEffect(() => {
    if (columnsFilterKeysMapping) {
      highlightFilteredColumns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return {
    columns,
    pagination,
    onTableChange,
    onSortChange,
    refreshTable,
    setFilter: newFilter => {
      setFilter({ ...constantFilter, ...newFilter });
    },
    isFiltered,
  };
};
